import { HowWeKnow } from './HowWeKnow'
import type { CoplayData } from '@/types'

interface PacingBarProps {
  coplay: CoplayData
}

export function PacingBar({ coplay }: PacingBarProps) {
  if (!coplay.hasStory) {
    const sessionsPerWeek = coplay.recentSessions / 2
    const signals = [
      `${coplay.sessions.length} inferred co-play session${coplay.sessions.length === 1 ? '' : 's'} total`,
      coplay.recentSessions > 0
        ? `${coplay.recentSessions} session${coplay.recentSessions === 1 ? '' : 's'} in the last 2 weeks`
        : 'No recent group activity',
    ]
    return (
      <div className="cg-pace cg-pace-openended">
        <div className="cg-pace-header">
          <span className="cg-pace-title">Cadence</span>
          <span className="cg-pace-meta">
            No 'How long to beat' data
            <HowWeKnow signals={signals} confidence={Math.min(0.75, coplay.avgConfidence)} />
          </span>
        </div>
        <div className="cg-pace-cadence">
          <div className="cg-pace-cadence-cell">
            <div className="cg-pace-cadence-num">
              <span className="cg-pt-tilde">~</span>{sessionsPerWeek.toFixed(1)}
            </div>
            <div className="cg-pace-cadence-label">sessions / week</div>
          </div>
          <div className="cg-pace-cadence-cell">
            <div className="cg-pace-cadence-num">
              <span className="cg-pt-tilde">~</span>
              {((coplay.recentPaceHPerDay ?? 0) * 7).toFixed(1)}h
            </div>
            <div className="cg-pace-cadence-label">hours / week</div>
          </div>
          <div className="cg-pace-cadence-cell">
            <div className="cg-pace-cadence-num">{coplay.sessions.length}</div>
            <div className="cg-pace-cadence-label">total sessions</div>
          </div>
        </div>
      </div>
    )
  }

  const pct = Math.round((coplay.progress ?? 0) * 100)
  const finishStr = coplay.finishDate
    ? new Date(coplay.finishDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : null
  const signals = [
    `${coplay.totalGroupHours.toFixed(1)}h estimated together vs ~${coplay.storyH}h main story (HowLongToBeat)`,
    (coplay.recentPaceHPerDay ?? 0) > 0
      ? `Recent pace: ${((coplay.recentPaceHPerDay ?? 0) * 7).toFixed(1)}h/week`
      : 'No recent activity to extrapolate from',
  ]
  return (
    <div className="cg-pace">
      <div className="cg-pace-header">
        <span className="cg-pace-title">Pacing</span>
        <span className="cg-pace-meta">
          <span className="cg-pt-tilde">~</span>{pct}% through main story
          <HowWeKnow signals={signals} confidence={Math.min(0.7, coplay.avgConfidence)} />
        </span>
      </div>
      <div className="cg-pace-track">
        <div className="cg-pace-fill" style={{ width: `${pct}%` }} />
      </div>
      <div className="cg-pace-foot">
        {(coplay.recentPaceHPerDay ?? 0) > 0 && finishStr ? (
          <span>
            At ~{((coplay.recentPaceHPerDay ?? 0) * 7).toFixed(1)}h/week, on track to wrap around{' '}
            <strong>{finishStr}</strong>
          </span>
        ) : (
          <span>Pace not yet established — log a few more sessions.</span>
        )}
      </div>
    </div>
  )
}
